import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { selectAdminToken } from "../../sessionSlice";
import { getReports } from "../../../functions/crossroadsApi";
import { formatDuration } from "../../../utils/crossroadsTheme";
import "./Reports.css";

function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header, ...rows].map((r) => r.map(esc).join(","));
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const fmtPct = (n) => (n == null ? "—" : `${n}%`);
const fmtTime = (s) => (s ? formatDuration(s) : "—");

function Kpi({ label, value, sub }) {
  return (
    <div className="rp-kpi">
      <div className="rp-kpi-value">{value}</div>
      <div className="rp-kpi-label">{label}</div>
      {sub ? <div className="rp-kpi-sub">{sub}</div> : null}
    </div>
  );
}

function PctBar({ value, label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="rp-bar" title={label}>
      <span className="rp-bar-track" aria-hidden="true">
        <span className="rp-bar-fill" style={{ width: `${v}%` }} />
      </span>
      <span className="rp-bar-value">{fmtPct(value)}</span>
    </div>
  );
}

/** Admin reporting & analytics (PRD 8). */
export default function Reports() {
  const token = useSelector(selectAdminToken);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError("");
    getReports({ token })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const participants = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data?.participants || [];
    return q ? list.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(q)) : list;
  }, [data, search]);

  if (!token) return <div className="rp-page"><p className="rp-empty">Open the admin panel from your game to see reports.</p></div>;

  const m = data?.metrics;

  const exportParticipants = () =>
    downloadCsv(
      "crossroads_participants.csv",
      ["Name", "Email", "Mode", "Status", "Score", "Correct", "Incorrect", "Accuracy %", "Completion time", "Feedback"],
      (data?.participants || []).map((p) => [
        p.name,
        p.email,
        p.mode === "live" ? "Live" : p.mode ? "Self Paced" : "",
        p.status,
        p.score,
        p.correct,
        p.incorrect,
        p.accuracy ?? "",
        p.timeSeconds ? formatDuration(p.timeSeconds) : "",
        p.feedback || "",
      ])
    );

  const exportQuestions = () =>
    downloadCsv(
      "crossroads_question_performance.csv",
      ["#", "Question", "Option A", "Option B", "Responses", "Chose A", "Chose A %", "Chose B", "Chose B %", "Correct %", "Most selected", "Avg response (s)"],
      (data?.questions || []).map((q) => [
        q.index + 1,
        q.title,
        q.optionA,
        q.optionB,
        q.total,
        q.a,
        q.aPct,
        q.b,
        q.bPct,
        q.total ? q.correctPct ?? "Choice (no correct answer)" : "No answers yet",
        q.mostSelected,
        q.avgResponseSeconds ?? "",
      ])
    );

  const exportLive = () =>
    downloadCsv(
      "crossroads_live_voting.csv",
      ["Run", "Started", "#", "Question", "Option A", "Option B", "Correct path", "Votes A", "A %", "Votes B", "B %", "Total votes", "Audience correct %", "Participants"],
      (data?.live || []).flatMap((run, r) =>
        run.questions.map((q) => [
          r + 1,
          new Date(run.startedAt).toLocaleString(),
          q.index + 1,
          q.title,
          q.optionA,
          q.optionB,
          q.correct || "Choice",
          q.votesA,
          q.aPct,
          q.votesB,
          q.bPct,
          q.totalVotes,
          q.correctPct ?? "",
          q.participants,
        ])
      )
    );

  return (
    <div className="rp-page">
      <div className="rp-head">
        <div>
          <h1>Reports</h1>
          <p>
            {data?.theme ? (
              <>
                Results for the theme <strong>{data.theme.title}</strong> in this session (other themes this session used are
                not included).{" "}
              </>
            ) : (
              "Results for this session. "
            )}
            Preview plays are never recorded.
          </p>
        </div>
        <button type="button" className="rp-btn" onClick={load} disabled={loading}>
          <i className="fa-solid fa-rotate" aria-hidden /> {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error ? <p className="rp-error" role="alert">{error}</p> : null}
      {!data && loading ? <p className="rp-empty">Loading reports…</p> : null}

      {m ? (
        <>
          <section className="rp-kpis" data-tour="reports-metrics" aria-label="Summary">
            <Kpi label="Total participants" value={m.totalParticipants} sub={m.liveParticipants ? `${m.liveParticipants} in live voting` : null} />
            <Kpi label="Completion rate" value={fmtPct(m.completionRate)} sub={`${m.completed} completed`} />
            <Kpi label="Average score" value={m.averageScore} />
            <Kpi label="Average accuracy" value={fmtPct(m.averageAccuracy)} />
            <Kpi label="Avg completion time" value={fmtTime(m.averageTimeSeconds)} />
          </section>

          <section className="rp-card">
            <div className="rp-card-head">
              <h2>Question-wise performance</h2>
              <button type="button" className="rp-btn" onClick={exportQuestions} disabled={!data.questions.length}>
                <i className="fa-solid fa-file-csv" aria-hidden /> Export CSV
              </button>
            </div>
            {data.questions.length ? (
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Responses</th>
                      <th>Path A</th>
                      <th>Path B</th>
                      <th>Most selected</th>
                      <th className="rp-col-bar">Answered correctly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.questions.map((q) => (
                      <tr key={q.questionId}>
                        <td>{q.index + 1}</td>
                        <td>
                          <strong>{q.title}</strong>
                        </td>
                        <td>{q.total}</td>
                        <td title={q.optionA}>
                          {q.a} <span className="rp-muted">({fmtPct(q.aPct)})</span>
                        </td>
                        <td title={q.optionB}>
                          {q.b} <span className="rp-muted">({fmtPct(q.bPct)})</span>
                        </td>
                        <td>{q.mostSelected}</td>
                        <td className="rp-col-bar">
                          {!q.total ? (
                            <span className="rp-muted">No answers yet</span>
                          ) : q.correctPct == null ? (
                            <span className="rp-muted">Choice — no correct answer</span>
                          ) : (
                            <PctBar value={q.correctPct} label={`${q.correct} of ${q.total} chose the correct path`} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rp-empty">No self-paced answers yet.</p>
            )}
          </section>

          <section className="rp-card">
            <div className="rp-card-head">
              <h2>Participants ({data.participants.length})</h2>
              <div className="rp-card-tools">
                <input
                  type="search"
                  className="rp-search"
                  placeholder="Search name or email"
                  aria-label="Search participants"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button type="button" className="rp-btn" onClick={exportParticipants} disabled={!data.participants.length}>
                  <i className="fa-solid fa-file-csv" aria-hidden /> Export CSV
                </button>
              </div>
            </div>
            {participants.length ? (
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Mode</th>
                      <th>Score</th>
                      <th>Correct</th>
                      <th>Incorrect</th>
                      <th>Accuracy</th>
                      <th>Time</th>
                      <th>Feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.userId}>
                        <td>
                          <strong>{p.name || "—"}</strong>
                          {p.email ? <span className="rp-sub">{p.email}</span> : null}
                        </td>
                        <td>
                          <span className={`rp-status${p.status === "Completed" ? " is-done" : ""}`}>
                            {p.status === "Completed" ? "✓ " : "… "}
                            {p.status}
                          </span>
                        </td>
                        <td>{p.mode === "live" ? "Live" : p.mode ? "Self Paced" : "—"}</td>
                        <td>{p.score}</td>
                        <td>{p.correct}</td>
                        <td>{p.incorrect}</td>
                        <td>{fmtPct(p.accuracy)}</td>
                        <td>{fmtTime(p.timeSeconds)}</td>
                        <td className="rp-feedback" title={p.feedback || ""}>
                          {p.feedback || <span className="rp-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rp-empty">{search ? "No participants match your search." : "No participants yet."}</p>
            )}
          </section>

          <section className="rp-card">
            <div className="rp-card-head">
              <h2>Live audience voting</h2>
              <button type="button" className="rp-btn" onClick={exportLive} disabled={!data.live.length}>
                <i className="fa-solid fa-file-csv" aria-hidden /> Export CSV
              </button>
            </div>
            {data.live.length ? (
              data.live.map((run, r) => (
                <div className="rp-run" key={run.runId}>
                  <div className="rp-run-head">
                    <strong>Run {r + 1}</strong>
                    <span className="rp-muted">{new Date(run.startedAt).toLocaleString()}</span>
                    <span>{run.participants} participants</span>
                    <span>{run.totalVotes} votes</span>
                    <span>Audience accuracy {fmtPct(run.accuracy)}</span>
                  </div>
                  <div className="rp-table-wrap">
                    <table className="rp-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Question</th>
                          <th>Correct path</th>
                          <th>Path A votes</th>
                          <th>Path B votes</th>
                          <th className="rp-col-bar">Voted correctly</th>
                        </tr>
                      </thead>
                      <tbody>
                        {run.questions.map((q) => (
                          <tr key={q.questionId}>
                            <td>{q.index + 1}</td>
                            <td>
                              <strong>{q.title}</strong>
                            </td>
                            <td>{q.correct || <span className="rp-muted">Choice</span>}</td>
                            <td title={q.optionA}>
                              {q.votesA} <span className="rp-muted">({fmtPct(q.aPct)})</span>
                            </td>
                            <td title={q.optionB}>
                              {q.votesB} <span className="rp-muted">({fmtPct(q.bPct)})</span>
                            </td>
                            <td className="rp-col-bar">
                              {q.correctPct == null ? (
                                <span className="rp-muted">—</span>
                              ) : (
                                <PctBar value={q.correctPct} label={`${q.correctPct}% of ${q.totalVotes} votes were correct`} />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            ) : (
              <p className="rp-empty">No live games have been run yet.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
